import type { SVGProps } from 'react';
import { Link } from 'react-router-dom';
import {
  IconBox,
  IconCheck,
  IconComment,
  IconCompare,
  IconDraw,
  IconPin,
  IconSearch,
  IconText,
  IconUpload,
} from '../components/icons';
import { PRICING_TIERS } from '../lib/plans';

/**
 * Public marketing/landing page — the front door for people who want to sign
 * up. Deliberately light (white) and quiet: generous whitespace, hairline
 * borders, one accent colour. Served at the site root (/); authenticated
 * visitors are redirected into the app.
 */
export function LandingPage() {
  return (
    <div className="min-h-full overflow-x-hidden bg-white text-neutral-900">
      {/* ── Nav ─────────────────────────────────────────────────────────── */}
      <header className="mx-auto flex max-w-6xl items-center justify-between px-6 py-5">
        <span className="text-base font-semibold tracking-tight">KatSquare Draft</span>
        <nav className="flex items-center gap-1.5">
          <a
            href="#pricing"
            className="hidden rounded-md px-3 py-2 text-sm font-medium text-neutral-600 hover:text-neutral-900 sm:inline-block"
          >
            Pricing
          </a>
          <Link
            to="/login"
            className="rounded-md px-3 py-2 text-sm font-medium text-neutral-600 hover:text-neutral-900"
          >
            Sign in
          </Link>
          <Link
            to="/register"
            className="rounded-md bg-neutral-900 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-neutral-800"
          >
            Get started
          </Link>
        </nav>
      </header>

      {/* ── Hero ────────────────────────────────────────────────────────── */}
      <section className="mx-auto max-w-3xl px-6 pb-4 pt-16 text-center sm:pt-24">
        <span className="inline-block rounded-full border border-neutral-200 px-3 py-1 text-xs font-medium text-neutral-500">
          Collaborative proofing for magazine teams
        </span>
        <h1 className="mt-6 text-4xl font-bold leading-tight tracking-tight sm:text-5xl">
          Approve every page with confidence.
        </h1>
        <p className="mx-auto mt-5 max-w-xl text-lg leading-relaxed text-neutral-500">
          KatSquare Draft is a shared proofing room for your PDFs. Mark up pages, compare
          revisions, and clear approval gates — all in one place your whole team can see.
        </p>
        <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <Link
            to="/register"
            className="w-full rounded-md bg-neutral-900 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-neutral-800 sm:w-auto"
          >
            Create your account
          </Link>
          <Link
            to="/login"
            className="w-full rounded-md border border-neutral-200 px-5 py-2.5 text-sm font-semibold text-neutral-700 transition-colors hover:bg-neutral-50 sm:w-auto"
          >
            Sign in
          </Link>
        </div>
        <p className="mt-4 text-xs text-neutral-400">Free plan included — no card required.</p>
      </section>

      {/* ── Product preview ─────────────────────────────────────────────── */}
      <section className="mx-auto max-w-4xl px-6 pb-16 pt-10 sm:pb-24 sm:pt-12">
        <PreviewPanel />
      </section>

      {/* ── Features ────────────────────────────────────────────────────── */}
      <section className="border-t border-neutral-100">
        <div className="mx-auto max-w-6xl px-6 py-16 sm:py-20">
          <div className="mx-auto max-w-xl text-center">
            <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">
              Everything a review needs
            </h2>
            <p className="mt-3 text-neutral-500">
              From first draft to final sign-off, without the endless email threads.
            </p>
          </div>
          <div className="mt-10 grid sm:mt-14 gap-x-10 gap-y-12 sm:grid-cols-2 lg:grid-cols-3">
            {FEATURES.map((f) => (
              <div key={f.title}>
                <div className="flex h-10 w-10 items-center justify-center rounded-lg border border-neutral-200 text-neutral-700">
                  <f.Icon className="h-5 w-5" />
                </div>
                <h3 className="mt-4 text-base font-semibold">{f.title}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-neutral-500">{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── How it works ────────────────────────────────────────────────── */}
      <section className="border-t border-neutral-100">
        <div className="mx-auto max-w-5xl px-6 py-16 sm:py-20">
          <div className="mx-auto max-w-xl text-center">
            <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">Three steps to sign-off</h2>
            <p className="mt-3 text-neutral-500">Upload, review together, and lock the approval.</p>
          </div>
          <ol className="mt-10 grid gap-10 sm:mt-14 sm:grid-cols-3">
            {STEPS.map((s, i) => (
              <li key={s.title}>
                <div className="flex items-center gap-3">
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-neutral-900 text-sm font-semibold text-white">
                    {i + 1}
                  </span>
                  <h3 className="text-base font-semibold">{s.title}</h3>
                </div>
                <p className="mt-3 text-sm leading-relaxed text-neutral-500">{s.body}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* ── Pricing ─────────────────────────────────────────────────────── */}
      <section id="pricing" className="scroll-mt-6 border-t border-neutral-100">
        <div className="mx-auto max-w-6xl px-6 py-16 sm:py-20">
          <div className="mx-auto max-w-xl text-center">
            <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">
              Simple, transparent pricing
            </h2>
            <p className="mt-3 text-neutral-500">
              Start free. Upgrade when your review process grows — change plans anytime.
            </p>
          </div>
          <div className="mt-10 grid sm:mt-14 gap-6 md:grid-cols-3">
            {PRICING_TIERS.map((tier) => (
              <div
                key={tier.plan}
                className={`relative flex flex-col rounded-2xl border bg-white p-6 ${
                  tier.popular ? 'border-neutral-900 shadow-sm' : 'border-neutral-200'
                }`}
              >
                {tier.popular && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-neutral-900 px-3 py-0.5 text-xs font-semibold text-white">
                    Most popular
                  </span>
                )}
                <h3 className="text-lg font-semibold">{tier.name}</h3>
                <p className="mt-1 text-sm text-neutral-500">{tier.tagline}</p>
                <div className="mt-4 flex items-baseline gap-1">
                  <span className="text-3xl font-bold tracking-tight">{tier.price}</span>
                  <span className="text-sm text-neutral-400">{tier.period}</span>
                </div>
                <ul className="mt-5 flex-1 space-y-2.5">
                  {tier.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-neutral-600">
                      <IconCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />
                      {f}
                    </li>
                  ))}
                </ul>
                <Link
                  to="/register"
                  className={`mt-6 block rounded-md py-2.5 text-center text-sm font-semibold transition-colors ${
                    tier.popular
                      ? 'bg-neutral-900 text-white hover:bg-neutral-800'
                      : 'border border-neutral-200 text-neutral-700 hover:bg-neutral-50'
                  }`}
                >
                  Get started
                </Link>
              </div>
            ))}
          </div>
          <p className="mx-auto mt-8 max-w-xl text-center text-xs text-neutral-400">
            Prices are illustrative for this demo — switching a plan takes effect immediately, no
            card required.
          </p>
        </div>
      </section>

      {/* ── FAQ ─────────────────────────────────────────────────────────── */}
      <section className="border-t border-neutral-100">
        <div className="mx-auto max-w-3xl px-6 py-16 sm:py-20">
          <h2 className="text-center text-2xl font-bold tracking-tight sm:text-3xl">
            Questions, answered
          </h2>
          <dl className="mt-12 divide-y divide-neutral-100">
            {FAQ.map((item) => (
              <div key={item.q} className="py-5">
                <dt className="text-base font-semibold">{item.q}</dt>
                <dd className="mt-2 text-sm leading-relaxed text-neutral-500">{item.a}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      {/* ── Closing CTA ─────────────────────────────────────────────────── */}
      <section className="border-t border-neutral-100">
        <div className="mx-auto max-w-3xl px-6 py-16 sm:py-20 text-center">
          <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">
            Ready to run your next issue?
          </h2>
          <p className="mx-auto mt-3 max-w-md text-neutral-500">
            Create a free account and upload your first proof in under a minute.
          </p>
          <div className="mt-8">
            <Link
              to="/register"
              className="inline-block rounded-md bg-neutral-900 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-neutral-800"
            >
              Create your account
            </Link>
          </div>
        </div>
      </section>

      {/* ── Footer ──────────────────────────────────────────────────────── */}
      <footer className="border-t border-neutral-100">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-3 px-6 py-8 sm:flex-row">
          <span className="text-sm font-medium text-neutral-600">KatSquare Draft</span>
          <nav className="flex items-center gap-4 text-xs text-neutral-500">
            <Link to="/terms" className="hover:text-neutral-900">
              Terms of Service
            </Link>
            <Link to="/privacy" className="hover:text-neutral-900">
              Privacy Policy
            </Link>
          </nav>
          <p className="text-xs text-neutral-400">Your proofs stay private to your workspace.</p>
        </div>
      </footer>
    </div>
  );
}

// ── Content ──────────────────────────────────────────────────────────────

const FEATURES: { Icon: (p: SVGProps<SVGSVGElement>) => JSX.Element; title: string; body: string }[] =
  [
    {
      Icon: IconPin,
      title: 'Annotate on the page',
      body: 'Drop pins, boxes, and freehand marks exactly where the change belongs. Every note is a threaded conversation.',
    },
    {
      Icon: IconCompare,
      title: 'Compare any two versions',
      body: 'Slide between rounds to see precisely what moved. Open comments carry forward to the next version automatically.',
    },
    {
      Icon: IconCheck,
      title: 'Structured approval gates',
      body: 'Route each proof through ordered steps — copy edit, art, client sign-off — with the approval rule you choose.',
    },
    {
      Icon: IconSearch,
      title: 'Search the real text',
      body: 'Text is extracted on upload, so you can find and quote any line — not just eyeball a flat image.',
    },
    {
      Icon: IconComment,
      title: 'One shared review room',
      body: 'Owners, admins, members, and reviewers each get the right access. No more PDFs scattered across inboxes.',
    },
    {
      Icon: IconUpload,
      title: 'Upload and it just renders',
      body: 'PDFs and images become crisp, deep-zoomable pages in seconds — no plugins and nothing to download.',
    },
  ];

const STEPS: { title: string; body: string }[] = [
  {
    title: 'Upload',
    body: 'Drop in a PDF or image. It renders into sharp, zoomable pages ready for review.',
  },
  {
    title: 'Review',
    body: 'Your team marks up pages and resolves comments together, round after round.',
  },
  {
    title: 'Approve',
    body: 'Clear each gate and lock the final sign-off, with a full activity trail behind it.',
  },
];

const FAQ: { q: string; a: string }[] = [
  {
    q: 'What can I upload?',
    a: 'PDFs and common image formats (PNG, JPEG, TIFF, WebP). Every file is rendered into sharp, deep-zoomable pages automatically — nothing to install.',
  },
  {
    q: 'Is my work kept private?',
    a: 'Every proof, comment, and approval is scoped to your workspace and visible only to the people you invite — nothing is public or shared by default.',
  },
  {
    q: 'How do approval gates work?',
    a: 'Each proof moves through the review steps you define — satisfied by any one approver, everyone, or a set quorum — and locks once every gate is cleared.',
  },
  {
    q: 'Can I invite clients or freelancers?',
    a: 'Add them as reviewers for comment-and-approve access on the proofs that matter, without handing over the rest of your workspace.',
  },
  {
    q: 'What happens when I reach a plan limit?',
    a: 'You get a clear message explaining the limit, and can upgrade in a click or free up room by removing older versions. Changing plans takes effect immediately.',
  },
];

// ── Bits ─────────────────────────────────────────────────────────────────

/** A quiet, stylised preview of the review surface — pure CSS, no assets. */
function PreviewPanel() {
  return (
    <div className="overflow-hidden rounded-xl border border-neutral-200 bg-white shadow-sm">
      {/* window chrome */}
      <div className="flex items-center gap-1.5 border-b border-neutral-100 px-4 py-3">
        <span className="h-2.5 w-2.5 rounded-full bg-neutral-200" />
        <span className="h-2.5 w-2.5 rounded-full bg-neutral-200" />
        <span className="h-2.5 w-2.5 rounded-full bg-neutral-200" />
        <span className="ml-3 text-xs font-medium text-neutral-400">
          Cover story · Issue 12 · v3
        </span>
        <span className="ml-auto rounded bg-neutral-900 px-2 py-0.5 text-[10px] font-semibold text-white">
          In review
        </span>
      </div>
      <div className="grid sm:grid-cols-[1fr_15rem]">
        {/* the review canvas */}
        <div className="relative border-b border-neutral-100 bg-neutral-50 p-5 sm:border-b-0 sm:border-r sm:p-8">
          {/* floating tool rail — mirrors the app's toolbar (desktop only) */}
          <div className="absolute right-3 top-1/2 z-10 hidden -translate-y-1/2 flex-col gap-0.5 rounded-lg border border-neutral-200 bg-white p-1 shadow-sm sm:flex">
            {[IconText, IconPin, IconBox, IconDraw].map((Ic, i) => (
              <span
                key={i}
                className={`flex h-7 w-7 items-center justify-center rounded-md ${
                  i === 0 ? 'bg-neutral-900 text-white' : 'text-neutral-400'
                }`}
              >
                <Ic className="h-4 w-4" />
              </span>
            ))}
          </div>

          {/* a magazine page mid-review */}
          <div className="relative mx-auto max-w-[19rem] rounded-md border border-neutral-200 bg-white shadow-sm">
            {/* masthead */}
            <div className="border-b border-neutral-100 px-5 pb-4 pt-5">
              <div className="h-1.5 w-14 rounded-full bg-blue-500/70" />
              <div className="mt-2.5 h-3 w-4/5 rounded bg-neutral-300" />
              <div className="mt-1.5 h-3 w-3/5 rounded bg-neutral-300" />
            </div>
            {/* hero image with a box annotation (marker 2) */}
            <div className="relative mx-5 mt-4 h-24 overflow-hidden rounded bg-gradient-to-br from-neutral-100 to-neutral-200">
              <div className="absolute right-4 top-4 h-9 w-16 rounded-sm border-2 border-blue-500 bg-blue-500/10" />
              <span className="absolute right-3 top-2 flex h-4 w-4 items-center justify-center rounded-full bg-blue-600 text-[8px] font-semibold text-white shadow">
                2
              </span>
            </div>
            {/* two-column body copy */}
            <div className="grid grid-cols-2 gap-4 p-5">
              <div className="space-y-2">
                {['w-full', 'w-full', 'w-5/6', 'w-full', 'w-2/3'].map((w, i) => (
                  <div key={i} className={`h-1.5 rounded bg-neutral-100 ${w}`} />
                ))}
              </div>
              <div className="space-y-2">
                {['w-full', 'w-4/5', 'w-full', 'w-full', 'w-1/2'].map((w, i) => (
                  <div key={i} className={`h-1.5 rounded bg-neutral-100 ${w}`} />
                ))}
              </div>
            </div>
            {/* pin marker (1) on the headline */}
            <div className="absolute -right-2.5 top-8 flex h-6 w-6 items-center justify-center rounded-full rounded-bl-none bg-blue-600 text-[10px] font-semibold text-white shadow">
              1
            </div>
          </div>
        </div>

        {/* the comment rail */}
        <div className="flex flex-col gap-3 p-4">
          <div className="flex items-center justify-between">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-neutral-400">
              Comments
            </p>
            <span className="rounded-full bg-neutral-100 px-1.5 py-0.5 text-[10px] font-semibold text-neutral-500">
              2 open
            </span>
          </div>

          {/* open comment, anchored to marker 1 */}
          <div className="rounded-lg border border-neutral-200 p-3">
            <div className="flex items-center gap-2">
              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-blue-600 text-[9px] font-semibold text-white">
                MK
              </span>
              <span className="text-xs font-medium text-neutral-700">Maya K.</span>
              <span className="ml-auto flex h-4 w-4 items-center justify-center rounded-full border border-blue-200 text-[9px] font-semibold text-blue-600">
                1
              </span>
            </div>
            <p className="mt-2 text-xs leading-relaxed text-neutral-500">
              Tighten the headline kerning — it feels loose against the image.
            </p>
          </div>

          {/* resolved comment */}
          <div className="rounded-lg border border-neutral-100 bg-neutral-50 p-3">
            <div className="flex items-center gap-2">
              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-neutral-300 text-[9px] font-semibold text-white">
                RS
              </span>
              <span className="text-xs font-medium text-neutral-500">Ravi S.</span>
              <span className="ml-auto inline-flex items-center gap-1 text-[10px] font-medium text-emerald-600">
                <IconCheck className="h-3 w-3" />
                Resolved
              </span>
            </div>
            <p className="mt-2 text-xs leading-relaxed text-neutral-400 line-through decoration-neutral-300">
              Swap in the hi-res cover crop.
            </p>
          </div>

          {/* approval gate cleared */}
          <div className="mt-auto flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2">
            <IconCheck className="h-4 w-4 shrink-0 text-emerald-600" />
            <span className="text-xs font-medium text-emerald-700">Art direction approved</span>
          </div>
        </div>
      </div>
    </div>
  );
}
